import React, { useState, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { useToastContext } from "../../../../context/ToastContext";

interface InlineEditOrganizationNameProps {
  isEditable?: boolean;
  memberCount?: number;
  onSave?: (newName: string) => Promise<void> | void;
}

const MAX_LENGTH = 50;
const MIN_LENGTH = 3;

function sanitizeInput(input: string): string {
  let cleaned = input.replace(/[^A-Za-z'\s]/g, "");
  if (cleaned.length > MAX_LENGTH) {
    cleaned = cleaned.slice(0, MAX_LENGTH);
  }
  return cleaned;
}

const InlineEditOrganizationName: React.FC<InlineEditOrganizationNameProps> = ({
  isEditable = false,
  memberCount = 0,
  onSave,
}) => {
  const { organizationOverview } = useAuth();
  const { showToast } = useToastContext();

  const initialOrgName =
    organizationOverview?.currentOrganization?.name || "Loading...";
  const [orgName, setOrgName] = useState(() => sanitizeInput(initialOrgName));
  const [tempOrgName, setTempOrgName] = useState(orgName);
  const [isEditing, setIsEditing] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (organizationOverview?.currentOrganization?.name) {
      setOrgName(sanitizeInput(organizationOverview.currentOrganization.name));
    }
  }, [organizationOverview?.currentOrganization?.name]);

  const handleStartEditing = () => {
    if (!isEditable) return;
    setIsEditing(true);
    setTempOrgName(orgName);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempOrgName(sanitizeInput(e.target.value));
  };

  const handleSave = async () => {
    const trimmed = tempOrgName.trim();
    if (trimmed === orgName) {
      setIsEditing(false);
      return;
    }
    if (trimmed.length < MIN_LENGTH) {
      showToast(
        "error",
        `Organization name must be at least ${MIN_LENGTH} characters.`
      );
      setTempOrgName(orgName);
    } else if (trimmed.length > MAX_LENGTH) {
      showToast(
        "error",
        `Organization name cannot exceed ${MAX_LENGTH} characters.`
      );
      setTempOrgName(orgName);
    } else {
      setOrgName(trimmed);
      if (onSave) await onSave(trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setTempOrgName(orgName);
      setIsEditing(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={tempOrgName}
          onChange={handleChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          autoFocus
          className="
            text-2xl font-bold
            bg-transparent
            border-0 border-b border-gray-300/30 dark:border-gray-600/30
            focus:border-primary dark:focus:border-secondary-dark
            focus:outline-none focus:ring-0
            text-text-primary dark:text-text-primaryDark
            px-0 py-0
            inline-block
            whitespace-nowrap
            overflow-auto
            max-w-[70vw]
          "
        />
      ) : (
        <div
          className={`group flex items-center cursor-pointer ${
            !isEditable && "pointer-events-none opacity-75"
          }`}
          onClick={handleStartEditing}
        >
          <span className="text-2xl font-bold text-text-primary dark:text-text-primaryDark">
            {orgName}
          </span>
          {isEditable && (
            <div className="w-4 h-4 flex items-center justify-center ml-2">
              <Pencil className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>
      )}
      <p className="text-base text-text-secondary dark:text-text-secondaryDark">
        This organization has <strong>{memberCount}</strong> members.
      </p>
    </div>
  );
};

export default InlineEditOrganizationName;
